import { useUser } from "~/Auth/useUser";
import { useRawJwt } from "~/Auth/AuthenticationManager";
import { WolfkrowUser } from "~/Auth/WolfkrowUser";
import { apiFetch, HttpError } from "~/api-fetch/apiFetch";

export type LoadFileProps = {
  imageUrl: string;
};

export type ImageInfo = {
  url: string;
  type: string;
  dispose: () => void;
};

type LoadFileArgs = LoadFileProps & {
  token: string | null;
  user: Pick<WolfkrowUser, "isImpersonating" | "email">;
};

export async function loadFile({
  imageUrl,
  token,
  user,
}: LoadFileArgs): Promise<ImageInfo> {
  const res = await apiFetch(
    imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`,
    {
      method: "get",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        impersonation: user.isImpersonating ? user.email : "",
      },
    },
    { operation: "loadFile" }
  );

  const message = "There was a problem loading the file";
  if (res.status !== 200) {
    throw HttpError.fromResponse(res, message);
  }
  if (!res.headers.get("Content-Type")) {
    throw new Error(message);
  }
  const type = res.headers.get("Content-Type")!;
  const blob = await res.blob();
  const file = new Blob([blob], { type: type as string });
  const fileUrl = URL.createObjectURL(file);
  return { url: fileUrl, type, dispose: () => URL.revokeObjectURL(fileUrl) };
}

export function useLoadFile(): (args: LoadFileProps) => Promise<ImageInfo> {
  const token = useRawJwt();
  const user = useUser();

  return ({ imageUrl }: LoadFileProps) => loadFile({ imageUrl, token, user });
}
